import React, { useCallback, useEffect, useState } from 'react';
import { ArrowCounterClockwise, CheckCircle, Gear, Printer, Scales, SlidersHorizontal } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Layout } from '../components/layout/Layout';
import { LoadingState, PageHeader } from '../components/app';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import {
  getItbmsPeriods, getItbmsReport, getItbmsSettings, presentItbmsPeriod, reopenItbmsPeriod,
  saveDgiControl, saveItbmsAdjustments, setPurchaseBuyerCheck, setSupplierPayeeType, updateItbmsSettings,
} from '../lib/api/taxReportsApi';
import {
  AdjustmentsDialog, DgiControlPanel, Form430Sheet, NoCreditAnnexSheet, OwnerGuide, PeriodList, PresentDialog, ReportDetail, SettingsDialog, WarningsPanel,
  dueLabel, formatMoney,
} from '../components/tax-reports';

const errorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg).join('; ');
  return fallback;
};

function Headline({ label, value, tone = 'text-[#F3F4F6]' }) {
  return (
    <div className="rounded-lg border border-[#242E42] bg-[#151B26] px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-[#9CA3AF]">{label}</p>
      <p className={`mt-1 font-['Outfit'] text-xl font-semibold tabular-nums ${tone}`}>B/. {formatMoney(value)}</p>
    </div>
  );
}

export default function TaxReportsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [periods, setPeriods] = useState([]);
  const [selected, setSelected] = useState('');
  const [report, setReport] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState('');

  const loadPeriods = useCallback(async ({ reset = false } = {}) => {
    const { data } = await getItbmsPeriods();
    setPeriods(data.periods);
    setSelected((current) => {
      const stillValid = data.periods.some((p) => p.period === current);
      if (current && stillValid && !reset) return current;
      const pending = data.periods.find((p) => p.period_ended && p.status === 'open');
      return (pending || data.periods[0]).period;
    });
    return data.periods;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [, settingsResponse] = await Promise.all([loadPeriods(), getItbmsSettings()]);
        setSettings(settingsResponse.data);
      } catch (error) {
        toast.error(errorMessage(error, 'No se pudo cargar el módulo de impuestos'));
      } finally {
        setLoading(false);
      }
    })();
  }, [loadPeriods]);

  const loadReport = useCallback(async (period) => {
    if (!period) return;
    setLoadingReport(true);
    try {
      const { data } = await getItbmsReport(period);
      setReport(data);
    } catch (error) {
      setReport(null);
      toast.error(errorMessage(error, 'No se pudo cargar el reporte'));
    } finally {
      setLoadingReport(false);
    }
  }, []);

  useEffect(() => { loadReport(selected); }, [selected, loadReport]);

  const run = async (action, success) => {
    setSaving(true);
    try {
      const { data } = await action();
      if (data?.period) setReport(data);
      await loadPeriods();
      setDialog('');
      if (success) toast.success(success);
      return data;
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo completar la acción'));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = async (form) => {
    setSaving(true);
    try {
      const { data } = await updateItbmsSettings(form);
      setSettings(data);
      setDialog('');
      toast.success('Datos guardados');
      await loadPeriods({ reset: true });
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudieron guardar los datos'));
    } finally {
      setSaving(false);
    }
  };

  const markBuyerCheck = async (row, status) => {
    try {
      await setPurchaseBuyerCheck(row.id, status);
      toast.success(status === 'missing' ? `${row.po_number}: sin crédito fiscal (gasto de la empresa)` : `${row.po_number} actualizada`);
      await loadReport(selected);
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo guardar la marca'));
    }
  };

  const markPayeeType = async (entry, payeeType) => {
    try {
      await setSupplierPayeeType(entry.supplier_id, payeeType);
      toast.success(`${entry.supplier_name}: ${payeeType === 'proveedor' ? 'proveedor (cuenta para crédito)' : 'pago fuera de las compras'}`);
      await loadReport(selected);
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo guardar el tipo de beneficiario'));
    }
  };

  const reopen = () => {
    if (!window.confirm('¿Reabrir este período? Se descartan las cifras congeladas y el saldo a favor deja de pasar al siguiente período.')) return;
    run(() => reopenItbmsPeriod(selected), 'Período reabierto');
  };

  if (loading) return <Layout><LoadingState /></Layout>;

  const form = report?.form || {};
  const canPresent = report && report.status === 'open' && report.period_ended;

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          icon={Scales}
          title="Impuestos · ITBMS"
          description="Borrador del Formulario 430 para tu contadora, con el detalle que lo respalda, y la guía del proceso."
          actions={(
            <Button variant="outline" onClick={() => setDialog('settings')} className="no-print border-[#242E42]"><Gear size={16} className="mr-2" /> Datos del contribuyente</Button>
          )}
        />

        <Tabs defaultValue="report">
          <TabsList className="no-print bg-[#151B26]">
            <TabsTrigger value="report">Reporte del período</TabsTrigger>
            <TabsTrigger value="guide">Guía del dueño</TabsTrigger>
          </TabsList>

          <TabsContent value="report" className="mt-4">
            <div className="grid gap-6 lg:grid-cols-[17rem_1fr]">
              <aside className="no-print">
                <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#9CA3AF]">Períodos</h2>
                <PeriodList periods={periods} selected={selected} onSelect={setSelected} />
              </aside>

              <div className="min-w-0 space-y-5">
                {loadingReport && !report ? <LoadingState /> : null}
                {report && (
                  <>
                    <div className="no-print flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <h2 className="font-['Outfit'] text-xl font-semibold text-[#F3F4F6]">{report.label}</h2>
                        <p className={`text-sm ${report.overdue ? 'font-semibold text-red-400' : 'text-[#9CA3AF]'}`}>
                          {report.start} al {report.end} · {dueLabel(report)}
                          {report.presented && ` · No. ${report.presented.declaration_number}`}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {report.status === 'open' && (
                          <Button variant="outline" onClick={() => setDialog('adjust')} className="border-[#242E42]"><SlidersHorizontal size={16} className="mr-2" /> Ajustes</Button>
                        )}
                        <Button variant="outline" onClick={() => window.print()} className="border-[#242E42]"><Printer size={16} className="mr-2" /> Imprimir / PDF</Button>
                        {canPresent && (
                          <Button onClick={() => setDialog('present')} className="bg-[#E2B05E] text-[#0B0E14] hover:bg-[#F0C47A]"><CheckCircle size={16} className="mr-2" /> Registrar presentación</Button>
                        )}
                        {report.frozen && isAdmin && (
                          <Button variant="outline" onClick={reopen} className="border-[#242E42]"><ArrowCounterClockwise size={16} className="mr-2" /> Reabrir</Button>
                        )}
                      </div>
                    </div>

                    {report.overdue && (
                      <p role="alert" className="no-print rounded-lg border border-red-500/50 bg-red-500/10 px-4 py-3 text-sm text-red-100">
                        Este período venció el {report.due_date} y no está registrado como presentado. Si ya se presentó, regístralo; si no, habla hoy con tu contadora:
                        pasado el día 15 aplica un recargo del 10% sobre el impuesto a pagar, más intereses.
                      </p>
                    )}
                    {report.frozen && report.differs_from_filed.length > 0 && (
                      <p role="alert" className="no-print rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
                        Desde que se presentó, los datos del ERP cambiaron: el cálculo de hoy difiere en las casillas {report.differs_from_filed.join(', ')}. La hoja muestra lo presentado.
                      </p>
                    )}

                    <div className="no-print grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <Headline label="Débito fiscal (211)" value={form['211']} />
                      <Headline label="Crédito fiscal + retención (45 + 52)" value={Number(form['45'] || 0) + Number(form['52'] || 0)} />
                      <Headline label="Total a pagar (59)" value={form['59']} tone={Number(form['59']) > 0 ? 'text-[#E2B05E]' : 'text-emerald-400'} />
                      <Headline label="Saldo a favor siguiente (54)" value={form['54']} />
                    </div>

                    <WarningsPanel warnings={report.warnings} />
                    <DgiControlPanel
                      report={report}
                      saving={saving}
                      onSave={(payload) => run(() => saveDgiControl(selected, payload), 'Control DGI guardado')}
                    />
                    <div className="overflow-x-auto pb-2">
                      <div className="tax-print-area space-y-6">
                        <Form430Sheet report={report} />
                        <NoCreditAnnexSheet report={report} />
                      </div>
                    </div>
                    <ReportDetail report={report} onBuyerCheck={markBuyerCheck} onPayeeType={markPayeeType} />
                  </>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="guide" className="mt-4"><OwnerGuide /></TabsContent>
        </Tabs>
      </div>

      <AdjustmentsDialog
        open={dialog === 'adjust'}
        onOpenChange={(open) => setDialog(open ? 'adjust' : '')}
        report={report}
        saving={saving}
        onSave={(payload) => run(() => saveItbmsAdjustments(selected, payload), 'Ajustes guardados')}
      />
      <PresentDialog
        open={dialog === 'present'}
        onOpenChange={(open) => setDialog(open ? 'present' : '')}
        report={report}
        saving={saving}
        onConfirm={(payload) => run(() => presentItbmsPeriod(selected, payload), 'Período cerrado')}
      />
      <SettingsDialog
        open={dialog === 'settings'}
        onOpenChange={(open) => setDialog(open ? 'settings' : '')}
        settings={settings}
        saving={saving}
        onSave={saveSettings}
      />
    </Layout>
  );
}
